import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import SmsIcon from "@mui/icons-material/SmsOutlined";
import { API_send_welcome_message } from "../../../../../constants";
import { postRightworkAPI } from "../../utilities/requests";
import { isValidSMSPhone } from "../../../../../helpers/helpers";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function formatPhoneForDisplay(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("52")) {
    return `+52 ${digits.slice(2, 4)} ${digits.slice(4, 8)} ${digits.slice(8)}`;
  }
  const ten = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (ten.length !== 10) return raw;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

function EmployeeNotLoggedInWarningModal(props) {
  const [welcomeSendCount, setWelcomeSendCount] = React.useState(
    props.welcomeSendCount || 0
  );
  const [isSending, setIsSending] = React.useState(false);
  const [channel, setChannel] = React.useState("email");
  // Gate on phone *validity*, not just presence — legacy / manually-edited
  // records may have malformed phone strings (e.g. "555555555a") that the
  // backend's normalization would reject and the manager would never know.
  const hasPhone = isValidSMSPhone(props.phoneNumber);
  const hasEmail = Boolean(props.emailAddress);
  // True iff the currently selected channel can actually deliver. Used to
  // disable the Send button so the manager can't fire a doomed request when
  // neither channel is available (or when the selected one isn't usable).
  const isSelectedChannelAvailable =
    (channel === "email" && hasEmail) || (channel === "sms" && hasPhone);
  const noChannelAvailable = !hasEmail && !hasPhone;

  React.useEffect(() => {
    if (props.open) {
      setWelcomeSendCount(props.welcomeSendCount || 0);
      // Default to whichever channel is actually available
      setChannel(hasEmail ? "email" : "sms");
      setIsSending(false);
    }
  }, [props.open, props.welcomeSendCount, hasEmail]);

  // Snap back to email if SMS is selected but phone is missing
  React.useEffect(() => {
    if (channel === "sms" && !hasPhone) {
      setChannel("email");
    }
  }, [channel, hasPhone]);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 460,
    maxHeight: 800,
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    p: 4,
    overflowY: "auto",
  };

  const send = async () => {
    if (isSending) return;
    const channelLabel = channel === "sms" ? "SMS" : "email";

    try {
      setIsSending(true);
      toast.loading(`Sending welcome ${channelLabel}...`, { autoClose: false });

      const res = await postRightworkAPI(API_send_welcome_message, {
        store_id: props.homeStore,
        stm_id: props.stm_id,
        channel,
      });

      if (res.status === 200) {
        toast.dismiss();
        toast.success(`Welcome ${channelLabel} sent successfully`, {
          autoClose: 3000,
        });
        setWelcomeSendCount((prev) => prev + 1);
        if (typeof props.onWelcomeEmailSent === "function") {
          props.onWelcomeEmailSent();
        }
        props.handleClose();
      }
    } catch (error) {
      toast.dismiss();
      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }
      console.error("Error:", error);
    } finally {
      setIsSending(false);
    }
  };

  // ChannelOption: a clickable card-like radio with icon, label, and destination
  const ChannelOption = ({ value, icon, label, destination, disabled, disabledHint }) => {
    const selected = channel === value;
    const baseClasses = "w-full flex items-center px-4 py-3 rounded-xl border transition-colors";
    const interactiveClasses = disabled
      ? "border-slate-200 bg-slate-50 cursor-not-allowed opacity-60"
      : selected
        ? "border-violet-700 bg-violet-50 cursor-pointer"
        : "border-slate-200 bg-white hover:bg-slate-50 cursor-pointer";
    return (
      <div
        role="radio"
        aria-checked={selected}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        className={`${baseClasses} ${interactiveClasses}`}
        onClick={() => {
          if (!disabled) setChannel(value);
        }}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setChannel(value);
          }
        }}
      >
        <div
          className={
            selected
              ? "h-9 w-9 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center mr-3"
              : "h-9 w-9 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mr-3"
          }
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center">
            <span className={selected ? "text-slate-900 font-medium" : "text-slate-700 font-medium"}>
              {label}
            </span>
          </div>
          <div className="text-xs text-slate-500 truncate">
            {disabled ? disabledHint : destination}
          </div>
        </div>
        <div className="ml-3 flex-shrink-0">
          <div
            className={
              selected
                ? "h-5 w-5 rounded-full border-2 border-violet-700 flex items-center justify-center"
                : "h-5 w-5 rounded-full border-2 border-slate-300"
            }
          >
            {selected && <div className="h-2.5 w-2.5 rounded-full bg-violet-700" />}
          </div>
        </div>
      </div>
    );
  };

  const sentCopy =
    welcomeSendCount > 0
      ? `We've sent ${welcomeSendCount} invite${welcomeSendCount === 1 ? "" : "s"}. Sending again will deliver a fresh link and replace the previous one.`
      : "No invite has been sent yet. We'll send a magic link they can use to set their password.";

  return (
    <Modal
      open={props.open}
      onClose={() => props.handleClose()}
      aria-labelledby="welcome-modal-title"
      aria-describedby="welcome-modal-description"
    >
      <Box sx={style}>
        <div
          className="absolute right-2 top-2 text-gray-500 cursor-pointer p-1"
          onClick={() => props.handleClose()}
        >
          <CloseIcon />
        </div>

        <div className="flex items-center space-x-2">
          <div className="text-orange-600">
            <WarningAmberIcon style={{ marginBottom: "1px" }} />
          </div>
          <h3 className="text-2xl text-slate-900" id="welcome-modal-title">
            No Log In Warning
          </h3>
        </div>

        <p className="text-slate-500 text-sm mt-4" id="welcome-modal-description">
          {`${props.firstName} ${props.lastName} hasn't signed in to Rightwork yet.`}
        </p>
        <p className="text-slate-500 text-sm mt-3">{sentCopy}</p>

        <div className="mt-5">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
            Send via
          </p>
          <div className="space-y-2" role="radiogroup" aria-label="Welcome message channel">
            <ChannelOption
              value="email"
              icon={<EmailIcon style={{ fontSize: 20 }} />}
              label="Email"
              destination={props.emailAddress || "No email on file"}
              disabled={!hasEmail}
              disabledHint="No email on file"
            />
            <ChannelOption
              value="sms"
              icon={<SmsIcon style={{ fontSize: 20 }} />}
              label="SMS"
              destination={formatPhoneForDisplay(props.phoneNumber)}
              disabled={!hasPhone}
              disabledHint={
                props.phoneNumber
                  ? "Phone number isn't supported for SMS"
                  : "Add a phone number to enable SMS"
              }
            />
          </div>
        </div>

        {noChannelAvailable && (
          <p className="text-rose-600 text-sm mt-4">
            Add an email or a valid SMS phone number to this employee's profile before sending an invite.
          </p>
        )}

        <div className="w-full flex justify-end mt-6 space-x-3">
          <button
            type="button"
            className="px-6 py-2 bg-white border border-violet-700 text-violet-700 hover:bg-slate-100 rounded-xl text-base"
            onClick={() => props.handleClose()}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isSending || !isSelectedChannelAvailable}
            className={
              isSending || !isSelectedChannelAvailable
                ? "px-6 py-2 bg-violet-300 text-white rounded-xl text-base cursor-not-allowed"
                : "px-6 py-2 bg-violet-700 hover:bg-violet-800 text-white rounded-xl text-base"
            }
            onClick={send}
          >
            {isSending ? "Sending…" : "Send invite"}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

export default EmployeeNotLoggedInWarningModal;
